<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useFbqSubscribeCardExposure } from '../composables/useChapterTracking'
import type { ChapterConfig, MemberTopupSubscribeProduct } from '@/composables/payment'
import { PaymentMethodSelector, useSubscription } from '@/composables/payment'
import { createShowPaymentMethodSelector } from '@/composables/payment/composables/usePaymentMethodDisplay'
import { CHARGE_TYPE, VIP_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'
import { SubscriptionCycle } from '@/utils/enum'
import { brand } from '@/config/brands'

interface Props {
  contentId: string
  contentChapterId: string
  config: ChapterConfig
  chapter: {
    priceAmount: number
    feeType: number
  }
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'fallback-unlock'): void
}>()
const { t } = useI18n()

const {
  loaded,
  getUnitPrice,
  onSubscribeProducts,
  availablePaymentMethods,
  selectPaymentMethod,
  currentPaymentMethod,
  currentSubscriptionProducts,
} = useSubscription('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const selectedProductId = ref<string | null>(null)
const isTalewaveDivider = computed(() => brand === 'talewave')
const talewaveLockDividerIcon = computed(() => `url(/images/${brand}/chapter/unlock-divider-lock.svg)`)

const subscribes = computed(() => {
  return (currentSubscriptionProducts.value || []).filter((item: any) => item && item.purchasePopShow)
})

// 订阅卡片曝光埋点：卡片可见 ≥50% 时上报 FB AddToCart（去重）
const { bindRef: bindFbqExposureRef } = useFbqSubscribeCardExposure()
// 缓存每个 item 的 ref setter，确保同一 item 永远返回同一函数引用，
// 避免内联箭头函数每次 render 创建新引用导致 Vue ref unbind/bind 死循环
const cardRefSetters = new Map<string, (el: any) => void>()
function getCardRefSetter(item: MemberTopupSubscribeProduct) {
  const key = String(item.productId ?? item.subscribeConfigId ?? '')
  if (!key)
    return undefined
  let setter = cardRefSetters.get(key)
  if (!setter) {
    setter = (el: any) => bindFbqExposureRef(item, el)
    cardRefSetters.set(key, setter)
  }
  return setter
}

function isDiscountMember(item: MemberTopupSubscribeProduct) {
  return item.subscribeType === VIP_TYPE.DISCOUNT
}

function hasPreferentialPrice(item: MemberTopupSubscribeProduct) {
  return item.preferentialPrice !== undefined && item.preferentialPrice !== null
}

function isFreeMember(item: MemberTopupSubscribeProduct) {
  if (!hasPreferentialPrice(item))
    return false

  return Number(item.preferentialPrice) === 0
}

function getDisplayPrice(item: MemberTopupSubscribeProduct) {
  if (isDiscountMember(item))
    return item.preferentialPrice || item.price

  if (hasPreferentialPrice(item) && !isFreeMember(item))
    return item.preferentialPrice

  return item.price
}

function getOriginalPrice(item: MemberTopupSubscribeProduct) {
  if (isDiscountMember(item))
    return item.discountOriginalPrice

  if (hasPreferentialPrice(item))
    return item.price

  return null
}

function handleSubscribe(item: MemberTopupSubscribeProduct) {
  selectedProductId.value = item.productId || item.subscribeConfigId || null
  selectPaymentMethod(currentPaymentMethod.value)
  onSubscribeProducts(item)
}

function getCycleLabel(item: MemberTopupSubscribeProduct) {
  return item.cycleStr || SubscriptionCycle.getItemByValue(item.cycle)?.label || ''
}

function getCycleDesc(item: MemberTopupSubscribeProduct) {
  return item.subscribeText || ''
}

function handleFallbackUnlock() {
  emit('fallback-unlock')
}
</script>

<template>
  <div class="subscribe-panel mt-24px">
    <div class="header-row">
      <span class="line" :class="{ 'line-talewave line-talewave-left': isTalewaveDivider }" />
      <span v-if="isTalewaveDivider" class="divider-icon-talewave" aria-hidden="true" />
      <i v-else class="i-icon-park-solid-lock text-18px text-#8C919D" />
      <span class="line" :class="{ 'line-talewave line-talewave-right': isTalewaveDivider }" />
    </div>
    <h3 class="panel-title">
      Unlock your membership and enjoy reading all books freely
    </h3>

    <div v-if="!loaded" class="loading-state">
      <BasicSpinner class="mx-auto" />
    </div>

    <div v-else-if="subscribes.length" class="content-block">
      <div
        v-for="item in subscribes"
        :key="item.productId || item.subscribeConfigId"
        :ref="getCardRefSetter(item)"
        class="subscribe-card"
        :class="{ selected: selectedProductId === (item.productId || item.subscribeConfigId) }"
        @click.stop="handleSubscribe(item)"
      >
        <div v-if="item.cornerTip && item.marketingCopy" class="discount-badge">
          {{ item.marketingCopy }}
        </div>

        <div class="card-row-1">
          <div class="row-left">
            <img v-if="props.config.colors.showVipIcon" :src="`/images/${brand}/vip/tw_ic_vip.png`" class="vip-icon">
            <span class="cycle-title">{{ getCycleLabel(item) }}</span>
          </div>
          <span v-if="isFreeMember(item)" class="price-main">{{ t('payTemplateFree') }}</span>
          <span v-else class="price-main">$ {{ getDisplayPrice(item) }}</span>
        </div>

        <div class="card-row-2">
          <span class="cycle-desc">{{ getCycleDesc(item) }}</span>
          <span v-if="getOriginalPrice(item)" class="price-sub price-sub--line-through">$ {{ getOriginalPrice(item) }}</span>
          <span v-else-if="getUnitPrice(item)" class="price-sub">$ {{ getUnitPrice(item) }}</span>
        </div>
      </div>

      <div v-if="showPaymentMethodSelector" @click.stop>
        <PaymentMethodSelector
          variant="large"
          :dark="false"
          align="start"
          transparent
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>
    </div>

    <a
      v-else
      class="fallback-btn inline-block mt-4 px-24px py-3 min-h-40px lh-1em text-15px text-white text-center btn-solid rounded-full w-300px max-w-full"
      @click.stop="handleFallbackUnlock"
    >
      <span v-if="chapter.priceAmount !== undefined">
        <span v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">{{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}</span>
        <span v-else>{{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}</span>
      </span>
    </a>
  </div>
</template>

<style lang="scss" scoped>
.subscribe-panel {
  margin: 0 -20px;
  padding: 0 14px 24px;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.line {
  width: 90px;
  height: 1px;
  background: linear-gradient(90deg, rgba(140, 145, 157, 0), rgba(140, 145, 157, 0.8), rgba(140, 145, 157, 0));
}

.line-talewave {
  width: 122px;
  max-width: 30vw;
  background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.line-talewave-left {
  background: linear-gradient(to left, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.line-talewave-right {
  background: linear-gradient(to right, var(--unlock-divider-line-color, #D2D6DE) 0%, var(--unlock-divider-line-color, #D2D6DE) 28%, transparent 100%);
}

.divider-icon-talewave {
  width: 22px;
  height: 22px;
  display: inline-block;
  flex-shrink: 0;
  background-color: var(--unlock-divider-color, #C2C9D3);
  -webkit-mask-image: v-bind(talewaveLockDividerIcon);
  mask-image: v-bind(talewaveLockDividerIcon);
  -webkit-mask-size: 20px 20px;
  mask-size: 20px 20px;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.panel-title {
  margin: 14px auto 0;
  width: 287px;
  height: 44px;
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: #32353A;
  text-align: center;
}

.loading-state {
  padding: 24px 0;
}

.content-block {
  margin-top: 19px;
}

.subscribe-card {
  position: relative;
  margin: 0 0 16px;
  min-height: 76px;
  padding: 13px;
  border: 2px solid rgba(253, 217, 194, 1);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(255, 246, 239, 1) 0%, rgba(255, 237, 222, 1) 100%);
}

.subscribe-card.selected {
  border-color: rgba(255, 171, 115, 1);
}

.card-row-1 {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.row-left {
  display: flex;
  align-items: center;
  gap: 7px;
}

.vip-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.cycle-title {
  color: rgba(136, 58, 15, 1);
  font-size: 18px;
  line-height: 14px;
  font-weight: 700;
}

.price-main {
  color: rgba(136, 58, 15, 1);
  font-size: 18px;
  line-height: 14px;
  font-weight: 700;
}

.card-row-2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.cycle-desc {
  color: rgba(182, 126, 76, 1);
  font-size: 14px;
  line-height: 12px;
}

.price-sub {
  color: rgba(182, 126, 76, 1);
  font-size: 12px;
  line-height: 12px;
}

.price-sub--line-through {
  text-decoration: line-through;
}

.discount-badge {
  position: absolute;
  top: -9px;
  left: -2px;
  height: 16px;
  padding: 0 8px;
  border-radius: 8px 2px 8px 2px;
  background: rgba(247, 73, 132, 1);
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  font-style: italic;
  font-weight: 600;
}

:deep(.payment-method-selector-large.start.transparent) {
  width: auto;
  margin: -16px 0 0;
  justify-content: flex-start;
}

:deep(.payment-method-btn-large) {
  width: 102px;
  min-width: 102px;
  height: 40px;
  padding: 8px 23px;
  border: 1px solid rgba(219, 219, 219, 1);
  border-radius: 8px;
  background: #fff;
}

:deep(.payment-method-btn-large.active) {
  border-color: rgba(83, 162, 255, 1);
  background: #fff;
}

:deep(.payment-method-btn-large.transparent) {
  background: #fff;
}

:deep(.payment-method-btn-large.active.transparent) {
  background: #fff;
}

:deep(.payment-icon-large) {
  height: 24px;
}
</style>
